<script>
export default {
  name: 'ClaimsRevisionList',
  filters: {
    toPounds(value) {
      if (isNaN(value)) return "----";
      return parseFloat(value).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  },
  props: {
    partners: {
      type: Array,
      required: true
    },
    claimPeriods: {
      type: Array,
      required: true
    },
    forecast: {
      type: Object,
      required: true
    },
    funding: {
      type: Object,
      required: true
    },
    project: {
      type: Object,
      required: true
    }
  },
  computed: {
    show() {
      return this.partners.length > 0 && this.claimPeriods.length > 0 && Object.keys(this.forecast).length > 0;
    },

    claimsByPartnerAndPeriod() {
      return this.partners.reduce((acc, partner) => {
        if (!acc[partner.company.name]) {
          acc[partner.company.name] = {};
        }

        acc[partner.company.name] = partner.claims.reduce((acc, claim) => {
          const [year, month] = claim.period.split("-");
          if (!acc[`${month}/${year}`]) {
            acc[`${month}/${year}`] = [];
          }
          acc[`${month}/${year}`].push(claim);
          return acc;
        }, {});
        return acc;
      }, {});
    },
  },
  methods: {
    viewLink(claim) {
      return `${window.location.href}/${claim.id}`;
    },

    /**
     * @description Get the claim status label and colour for the current claim. This is used to display the status of the claim in the UI.
     * @param {Claim} claim - The claim object to get the status for.
     * @returns {{label: string, colour: string}}
     */
    claimStatus(claim) {
      return {
        label: claim.status_label ?? 'Unknown',
        colour: (() => {
          switch(claim.status) {
            case 0: return 'primary';
            case 1:
            case 2: return 'neutral';
            case 3: return 'positive';
            case 4: return 'secondary';
            case 5: return 'negative';
            case 6:
            default: return 'grey';
          }
        })()
      }
    },

    /**
     * @description Calculate the total claim value for a given claim. This is used to display the total claim value in the UI.
     * @param {Claim} claim - The claim object to calculate the total claim value for.
     * @returns {number} - The total claim value.
     */
    totalClaim(claim, period) {
      const p = period.replace('/20', '/');
      const forecast = claim.form?.forecast?.[p];
      if (!forecast) {
        return 0;
      }
      return Object.values(forecast).reduce((total, { value }) => total + value || 0, 0) * .7;
    }
  },
}
/** @typedef {object} Claim
 * @property {number} id
 * @property {number} competition_id
 * @property {number} competition_project_id
 * @property {number} competition_project_partner_id
 * @property {null} previous_claim_id
 * @property {string} period
 * @property {number} version
 * @property {number} status
 * @property {null} submitted_by
 * @property {null} submitted_at
 * @property {null} reviewed_by
 * @property {null} reviewed_at
 * @property {null} approved_by
 * @property {null} approved_at
 * @property {null} review_comments
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} period_path
 * @property {boolean} is_editable
 * @property {boolean} is_open
 * @property {boolean} is_in_progress
 * @property {string} status_label
 * @property {boolean} is_reviewed
 * @property {boolean} is_approved
 * @property {boolean} can_complete_transactions
 */
</script>

<template>
  <div
    v-if="show"
    class="grid sub-grid col-span-full"
  >
    <section
      v-for="(claims, partner) in claimsByPartnerAndPeriod"
      :key="partner"
      class="col-span-full border bg-white shadow-lg rounded-b overflow-hidden"
    >
      <h2 class="py-2 px-6 border-b-2">
        {{ partner }}
      </h2>
      <details
        v-for="period in claimPeriods"
        :key="period"
        class="border-b-4"
        :open="claims[period] && claims[period].length > 0"
      >
        <summary class="text-sm py-2 px-6 border-b bg-grey-100 font-bold">
          {{ period }}
        </summary>
        <div
          v-if="claims[period] && claims[period].length > 0"
          class="grid"
          style="row-gap: 0"
        >
          <div
            v-for="claim in claims[period]"
            :key="claim.id"
            class="col-span-full py-2 px-6 border-b sub-grid text-sm"
            :class="{
              'bg-neutral-50': claim.status == 1,
              'bg-neutral-50': claim.status == 2,
              'bg-positive-50': claim.status == 3,
              'bg-primary-50': claim.status == 4,
              'bg-negative-50': claim.status == 5,
              'bg-grey-100': claim.status == 6,
              'opacity-75': claim.status > 3
            }"
          >
            <data-provider
              v-slot="{ data }"
              :source="claimStatus(claim)"
            >
              <app-badge
                :type="data.colour"
                class="text-center my-auto"
                slot-classes="py-2 block w-full h-full"
              >
                {{ data.label }}
              </app-badge>
            </data-provider>
            <dl class="col-span-9 sub-grid">
              <div class="col-span-2 border-r pr-4">
                <dt class="text-xs font-bold">
                  Submitted By
                </dt>
                <dd>
                  {{ claim.submittee ? claim.submittee.name : '-' }}
                </dd>
              </div>
              <div class="col-span-2 border-r pr-4">
                <dt class="text-xs font-bold">
                  MSP Reviewer
                </dt>
                <dd>
                  {{ claim.reviewee ? claim.reviewee.name : '-' }}
                </dd>
              </div>
              <div class="col-span-2 border-r">
                <dt class="text-xs font-bold">
                  PMO Reviewer
                </dt>
                <dd>
                  {{ claim.approvee ? claim.approvee.name : '-' }}
                </dd>
              </div>
              <div class="col-span-2">
                <dt class="text-xs font-bold">
                  Claim Value
                </dt>
                <dd>
                  £{{ totalClaim(claim, period) | toPounds }}
                </dd>
              </div>
            </dl>
            <app-button
              tag="a"
              class="col-span-2"
              :href="viewLink(claim)"
              :colour="[1,2,3].includes(claim.status) ? 'primary' : 'secondary'"
            >
              {{ claim.status == 1 ? 'Review' : 'View' }} Claim
            </app-button>
          </div>
        </div>
        <div
          v-else
          class="col-span-full py-2 px-6 border-b h-12 flex justify-center items-center"
        >
          <p>No claims for this period.</p>
        </div>
      </details>
      <forecast-matrix
        :can-edit="false"
        :forecast="forecast"
        :for-version="forecast.current.revision"
        :project-id="$page.project.uuid"
        :project-partner-id="claims.id * 10000"
        :intervention-rate="$page.competition.max_partner_grant_intervention_rate || 100"
        embedded
        class="bg-grey-100"
      />
    </section>
  </div>
</template>
